import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Avatar,
  Button,
  Chip,
  Skeleton,
  Alert,
  Tooltip,
  IconButton,
  Stack,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import Grid from "@mui/material/Grid2";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import FilterFramesIcon from "@mui/icons-material/FilterFrames";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CategoryIcon from "@mui/icons-material/Category";
import ListAltIcon from "@mui/icons-material/ListAlt";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SpeedIcon from "@mui/icons-material/Speed";
import TimelineIcon from "@mui/icons-material/Timeline";
import useLocalStorage from "../hooks/useLocalStorage";
import { getDashboardSummary, getDashboardDetail } from "../services/dashboardService";
import DrillDownView from "../components/DrillDownView";
import ERPGrid from "../components/ERPGrid";
import ERPSnakBar from "../components/ERPSnakBar";

type KpiColor = "primary" | "secondary" | "success" | "warning" | "error" | "info";

const dateFmt = (p: any) => (p.value ? dayjs(p.value).format("DD-MM-YYYY") : "-");
const numberFmt = (p: any) => (p.value || p.value === 0 ? Number(p.value).toLocaleString() : "-");
const moneyFmt = (p: any) =>
  p.value || p.value === 0
    ? Number(p.value).toLocaleString(undefined, { maximumFractionDigits: 2 })
    : "-";

const BUCKET_LABELS: Record<string, string> = {
  notDue: "Not Due",
  d1_30: "1-30 Days",
  d31_60: "31-60 Days",
  d61_90: "61-90 Days",
  d90plus: "90+ Days",
};

const drillLinkCol = (field: string, onClick: (value: string) => void): ColDef => ({
  field,
  cellClass: "drill-link",
  onCellClicked: (p: any) => p.value && onClick(String(p.value).split(",")[0].trim()),
});

const buildOrderColumns = (onOrderClick: (orderNo: string) => void): ColDef[] => [
  {
    ...drillLinkCol("order_no", onOrderClick),
    headerName: "Order No",
    width: 110,
    filter: true,
    sortable: true,
  },
  { field: "client_order_no", headerName: "Client Order No", width: 150, filter: true },
  { field: "vendor_order_no", headerName: "Vendor Order No", width: 150, filter: true },
  { field: "client_name", headerName: "Client", width: 130, filter: true },
  { field: "vendor_name", headerName: "Vendor", width: 130, filter: true },
  { field: "dated", headerName: "Order Date", width: 120, valueFormatter: dateFmt, sortable: true },
  { field: "ship_date", headerName: "Ship Date", width: 120, valueFormatter: dateFmt, sortable: true },
  { field: "ship_type", headerName: "Ship Type", width: 110, filter: true },
  { field: "ship_country", headerName: "Country", width: 110, filter: true },
  {
    field: "close",
    headerName: "Status",
    width: 100,
    cellDataType: "text",
    valueFormatter: (p: any) => (p.value ? "Closed" : "Open"),
  },
  { field: "remarks", headerName: "Remarks", minWidth: 160, flex: 1 },
];

const buildPendingShipmentColumns = (onOrderClick: (orderNo: string) => void): ColDef[] => [
  {
    ...drillLinkCol("order_no", onOrderClick),
    headerName: "Order No",
    width: 110,
    filter: true,
    sortable: true,
  },
  { field: "client_order_no", headerName: "Client Order No", width: 150, filter: true },
  { field: "client_name", headerName: "Client", width: 130, filter: true },
  { field: "vendor_name", headerName: "Vendor", width: 130, filter: true },
  { field: "dated", headerName: "Order Date", width: 120, valueFormatter: dateFmt },
  { field: "ship_date", headerName: "Ship Date", width: 120, valueFormatter: dateFmt },
  { field: "ordered_qty", headerName: "Ordered Qty", width: 120, valueFormatter: numberFmt, sortable: true },
  { field: "shipped_qty", headerName: "Shipped Qty", width: 120, valueFormatter: numberFmt },
  {
    field: "pending_qty",
    headerName: "Pending Qty",
    width: 130,
    valueFormatter: numberFmt,
    sortable: true,
    cellStyle: { fontWeight: 700 },
  },
  { field: "ordered_amount", headerName: "Ordered Amount", width: 140, valueFormatter: moneyFmt },
  { field: "shipped_amount", headerName: "Shipped Amount", width: 140, valueFormatter: moneyFmt },
  {
    field: "pending_amount",
    headerName: "Pending Amount",
    minWidth: 150,
    flex: 1,
    valueFormatter: moneyFmt,
  },
];

const buildShippingColumns = (onShipClick: (shipNo: string) => void): ColDef[] => [
  {
    ...drillLinkCol("ship_no", onShipClick),
    headerName: "Ship No",
    width: 130,
    filter: true,
    sortable: true,
  },
  { field: "ship_date", headerName: "Ship Date", width: 120, valueFormatter: dateFmt, sortable: true },
  { field: "order_no", headerName: "Order No", width: 130, filter: true },
  { field: "client_name", headerName: "Client", width: 120, filter: true },
  { field: "vendor_name", headerName: "Vendor", width: 130, filter: true },
  { field: "ship_type", headerName: "Ship Type", width: 110, filter: true },
  { field: "qty", headerName: "Qty", width: 100, valueFormatter: numberFmt, sortable: true },
  { field: "amount", headerName: "Amount", width: 130, valueFormatter: moneyFmt, sortable: true },
  { field: "bl_no", headerName: "BL No", width: 110, filter: true },
  { field: "bl_date", headerName: "BL Date", width: 110, valueFormatter: dateFmt },
  { field: "remarks", headerName: "Remarks", minWidth: 160, flex: 1 },
];

// Undelivered shipments contributing to a row's Transit Qty — same shape as
// the shipping grid, plus Order No drill into that order's line items.
const buildCategoryTransitColumns = (
  onOrderClick: (orderNo: string) => void,
  onShipClick: (shipNo: string) => void
): ColDef[] => [
  {
    ...drillLinkCol("ship_no", onShipClick),
    headerName: "Ship No",
    width: 130,
    filter: true,
    sortable: true,
  },
  { field: "ship_date", headerName: "Ship Date", width: 120, valueFormatter: dateFmt, sortable: true },
  {
    ...drillLinkCol("order_no", onOrderClick),
    headerName: "Order No",
    width: 130,
    filter: true,
  },
  { field: "client_name", headerName: "Client", width: 120, filter: true },
  { field: "vendor_name", headerName: "Vendor", width: 130, filter: true },
  { field: "ship_type", headerName: "Ship Type", width: 110, filter: true },
  { field: "qty", headerName: "Transit Qty", width: 120, valueFormatter: numberFmt, sortable: true },
  { field: "amount", headerName: "Amount", width: 130, valueFormatter: moneyFmt, sortable: true },
  { field: "bl_no", headerName: "BL No", width: 110, filter: true },
  { field: "bl_date", headerName: "BL Date", width: 110, valueFormatter: dateFmt },
  { field: "remarks", headerName: "Remarks", minWidth: 160, flex: 1 },
];

const buildPaymentColumns = (onShipClick: (shipNo: string) => void): ColDef[] => [
  {
    ...drillLinkCol("ship_no", onShipClick),
    headerName: "Invoice No",
    width: 130,
    filter: true,
    sortable: true,
  },
  { field: "ship_date", headerName: "Invoice Date", width: 120, valueFormatter: dateFmt, sortable: true },
  { field: "order_no", headerName: "Order No", width: 110, filter: true },
  { field: "client_name", headerName: "Client", width: 120, filter: true },
  { field: "vendor_name", headerName: "Vendor", width: 130, filter: true },
  { field: "total_amount", headerName: "Amount", width: 130, valueFormatter: moneyFmt, sortable: true },
  { field: "due_date", headerName: "Due Date", width: 120, valueFormatter: dateFmt, sortable: true },
  {
    field: "days_overdue",
    headerName: "Days Overdue",
    width: 130,
    sortable: true,
    valueFormatter: (p: any) => (p.value > 0 ? p.value : 0),
  },
  {
    field: "bucket",
    headerName: "Ageing",
    width: 120,
    valueFormatter: (p: any) => BUCKET_LABELS[p.value] || p.value,
  },
  { field: "payment_status", headerName: "Payment Status", width: 150, filter: true },
  { field: "payment_remarks", headerName: "Payment Remarks", minWidth: 160, flex: 1 },
];

const ORDER_LINE_COLUMNS: ColDef[] = [
  { field: "art_no", headerName: "Article No", width: 110, filter: true },
  { field: "art_name", headerName: "Article Name", width: 200, filter: true },
  { field: "color", headerName: "Color", width: 120, filter: true },
  { field: "size", headerName: "Size", width: 100, filter: true },
  { field: "barcode", headerName: "Barcode", width: 140, filter: true },
  { field: "qty", headerName: "Qty", width: 100, valueFormatter: numberFmt, sortable: true },
  { field: "price", headerName: "Price", width: 100, valueFormatter: moneyFmt },
  { field: "total", headerName: "Total", width: 120, valueFormatter: moneyFmt, sortable: true },
  { field: "shipped_qty", headerName: "Shipped Qty", width: 120, valueFormatter: numberFmt },
  {
    field: "balance_qty",
    headerName: "Balance Qty",
    minWidth: 130,
    flex: 1,
    valueFormatter: numberFmt,
  },
];

const SHIPMENT_LINE_COLUMNS: ColDef[] = [
  { field: "art_no", headerName: "Article No", width: 110, filter: true },
  { field: "art_name", headerName: "Article Name", width: 200, filter: true },
  { field: "color", headerName: "Color", width: 120, filter: true },
  { field: "size", headerName: "Size", width: 100, filter: true },
  { field: "qty", headerName: "Qty", width: 100, valueFormatter: numberFmt, sortable: true },
  { field: "price", headerName: "Price", width: 100, valueFormatter: moneyFmt },
  {
    field: "amount",
    headerName: "Amount",
    minWidth: 130,
    flex: 1,
    valueFormatter: moneyFmt,
    sortable: true,
  },
];

// The Category/Master/Detail levels all carry the same 6 metric columns in
// front of their own identifying columns. Drill targets: Net Prod. Qty opens
// the contributing order lines, Transit Qty opens undelivered shipments, and
// Open/Overdue Invoices open the contributing invoice lines — all scoped to
// whatever this row identifies (category / art_no). Open Amount and Overdue
// Amount remain plain display values.
const buildMetricColumns = (
  identifierCols: ColDef[],
  onQtyDrill: (row: any) => void,
  onTransitDrill: (row: any) => void,
  onInvoiceDrill: (row: any, filter?: string) => void
): ColDef[] => {
  const overdueStyle = (p: any) => (p.value > 0 ? { color: "#D32F2F", fontWeight: 700 } : undefined);
  return [
    ...identifierCols,
    {
      field: "order_count",
      headerName: "No. of Orders",
      width: 140,
      valueFormatter: numberFmt,
      sortable: true,
      cellClass: "drill-link",
      onCellClicked: (p: any) => onQtyDrill(p.data),
    },
    {
      field: "net_production_qty",
      headerName: "Net Prod. Qty",
      width: 150,
      valueFormatter: numberFmt,
      sortable: true,
      cellClass: "drill-link",
      onCellClicked: (p: any) => onQtyDrill(p.data),
    },
    {
      field: "transit_qty",
      headerName: "Transit Qty",
      width: 140,
      valueFormatter: numberFmt,
      sortable: true,
      cellClass: "drill-link",
      onCellClicked: (p: any) => onTransitDrill(p.data),
    },
    {
      field: "open_invoices",
      headerName: "Open Invoices",
      width: 140,
      valueFormatter: numberFmt,
      sortable: true,
      cellClass: "drill-link",
      onCellClicked: (p: any) => onInvoiceDrill(p.data),
    },
    {
      field: "open_amount",
      headerName: "Open Amount",
      width: 150,
      valueFormatter: moneyFmt,
      sortable: true,
    },
    {
      field: "overdue_invoices",
      headerName: "Overdue Invoices",
      width: 150,
      valueFormatter: numberFmt,
      sortable: true,
      cellStyle: overdueStyle,
      cellClass: "drill-link",
      onCellClicked: (p: any) => onInvoiceDrill(p.data, "overdue"),
    },
    {
      field: "overdue_amount",
      headerName: "Overdue Amount",
      minWidth: 160,
      flex: 1,
      valueFormatter: moneyFmt,
      sortable: true,
      cellStyle: overdueStyle,
    },
  ];
};

// Orders contributing to a row's Net Prod. Qty — one row per order_no (an
// order split across several shipments still shows once, Ship Nos and
// Shipment Dates comma-separated in the same order). Order No opens that
// order's own line items; Shipment No (if already shipped — takes the first
// one when there's more than one) opens that shipment's master info.
const buildCategoryOrderColumns = (
  onOrderClick: (orderNo: string) => void,
  onShipClick: (shipNo: string) => void
): ColDef[] => [
  {
    ...drillLinkCol("order_no", onOrderClick),
    headerName: "Order No",
    width: 140,
    filter: true,
    sortable: true,
  },
  { field: "order_date", headerName: "Order Date", width: 120, valueFormatter: dateFmt, sortable: true },
  {
    field: "order_ship_date",
    headerName: "Actual Shipment Date",
    width: 170,
    valueFormatter: dateFmt,
    sortable: true,
  },
  { field: "customer", headerName: "Customer", minWidth: 150, flex: 1, filter: true },
  {
    ...drillLinkCol("ship_no", onShipClick),
    headerName: "Shipment No",
    width: 160,
    filter: true,
    valueFormatter: (p: any) => p.value || "-",
  },
  {
    field: "ship_date",
    headerName: "Shipment Date",
    width: 160,
    valueFormatter: (p: any) => p.value || "-",
  },
  {
    field: "late_days",
    headerName: "Late Shipment (Days)",
    width: 170,
    valueFormatter: (p: any) => p.value ?? "-",
    cellStyle: (p: any) => {
      if (p.value === null || p.value === undefined) return undefined;
      const late = String(p.value)
        .split(",")
        .some((v: string) => Number(v.trim()) > 0);
      return late ? { color: "#D32F2F", fontWeight: 700 } : undefined;
    },
  },
  {
    field: "total_order_qty",
    headerName: "Total Order Qty",
    width: 140,
    valueFormatter: numberFmt,
    sortable: true,
  },
  {
    field: "net_production_qty",
    headerName: "Net Prod. Qty",
    width: 140,
    valueFormatter: numberFmt,
    sortable: true,
  },
  { field: "transit_qty", headerName: "In-Transit", width: 120, valueFormatter: numberFmt, sortable: true },
  { field: "shipped_qty", headerName: "Shipped Qty", width: 130, valueFormatter: numberFmt, sortable: true },
];

// Open invoice/shipment lines contributing to a row's Open/Overdue Invoices —
// Order No opens that order's line items, Ship No drills one further level
// into that shipment's full line items (the root).
const buildInvoiceLineColumns = (
  onOrderClick: (orderNo: string) => void,
  onShipClick: (shipNo: string) => void
): ColDef[] => [
  {
    ...drillLinkCol("ship_no", onShipClick),
    headerName: "Ship No",
    width: 130,
    filter: true,
    sortable: true,
  },
  { field: "ship_date", headerName: "Ship Date", width: 120, valueFormatter: dateFmt, sortable: true },
  {
    ...drillLinkCol("order_no", onOrderClick),
    headerName: "Order No",
    width: 110,
    filter: true,
  },
  { field: "vendor_name", headerName: "Vendor", width: 120, filter: true },
  { field: "client_name", headerName: "Client", width: 120, filter: true },
  { field: "amount", headerName: "Amount", width: 120, valueFormatter: moneyFmt, sortable: true },
  { field: "due_date", headerName: "Due Date", width: 120, valueFormatter: dateFmt, sortable: true },
  {
    field: "days_overdue",
    headerName: "Days Overdue",
    width: 130,
    sortable: true,
    valueFormatter: (p: any) => (p.value > 0 ? p.value : 0),
  },
  {
    field: "bucket",
    headerName: "Ageing",
    width: 120,
    valueFormatter: (p: any) => BUCKET_LABELS[p.value] || p.value,
  },
  { field: "payment_status", headerName: "Payment Status", width: 150, filter: true },
  { field: "payment_remarks", headerName: "Payment Remarks", minWidth: 160, flex: 1 },
];

type DrillDownState = {
  type: string;
  filter?: string;
  title: string;
  subtitle?: string;
  columns: ColDef[];
  params?: Record<string, string>;
};

const sumField = (rows: any[], field: string) =>
  rows.reduce((sum, r) => sum + (Number(r[field]) || 0), 0);

const computeTotals = (type: string | undefined, rows: any[]): any | null => {
  if (!rows?.length) return null;
  switch (type) {
    case "pendingShipment":
      return {
        order_no: "Total",
        ordered_qty: sumField(rows, "ordered_qty"),
        shipped_qty: sumField(rows, "shipped_qty"),
        pending_qty: sumField(rows, "pending_qty"),
        ordered_amount: sumField(rows, "ordered_amount"),
        shipped_amount: sumField(rows, "shipped_amount"),
        pending_amount: sumField(rows, "pending_amount"),
      };
    case "shipping":
      return {
        ship_no: "Total",
        qty: sumField(rows, "qty"),
        amount: sumField(rows, "amount"),
      };
    case "payment":
      return {
        ship_no: "Total",
        total_amount: sumField(rows, "total_amount"),
      };
    case "orderLines":
      return {
        art_no: "Total",
        qty: sumField(rows, "qty"),
        total: sumField(rows, "total"),
        shipped_qty: sumField(rows, "shipped_qty"),
        balance_qty: sumField(rows, "balance_qty"),
      };
    case "shipmentLines":
      return {
        art_no: "Total",
        qty: sumField(rows, "qty"),
        amount: sumField(rows, "amount"),
      };
    case "categoryMaster":
      return {
        art_no: "Total",
        order_count: sumField(rows, "order_count"),
        net_production_qty: sumField(rows, "net_production_qty"),
        transit_qty: sumField(rows, "transit_qty"),
        open_invoices: sumField(rows, "open_invoices"),
        open_amount: sumField(rows, "open_amount"),
        overdue_invoices: sumField(rows, "overdue_invoices"),
        overdue_amount: sumField(rows, "overdue_amount"),
      };
    case "categoryInvoices":
      return {
        ship_no: "Total",
        amount: sumField(rows, "amount"),
      };
    case "categoryOrders":
      return {
        order_no: "Total",
        total_order_qty: sumField(rows, "total_order_qty"),
        net_production_qty: sumField(rows, "net_production_qty"),
        transit_qty: sumField(rows, "transit_qty"),
        shipped_qty: sumField(rows, "shipped_qty"),
      };
    case "categoryTransit":
      return {
        ship_no: "Total",
        qty: sumField(rows, "qty"),
        amount: sumField(rows, "amount"),
      };
    default:
      return null;
  }
};

function KpiCard({
  icon,
  label,
  value,
  secondary,
  color = "primary",
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  secondary?: string;
  color?: KpiColor;
  onClick?: () => void;
}) {
  const content = (
    <CardContent sx={{ display: "flex", alignItems: "center", gap: 1.75, "&:last-child": { pb: 2 } }}>
      <Avatar
        variant="rounded"
        sx={{
          bgcolor: (theme) => alpha(theme.palette[color].main, 0.14),
          color: `${color}.main`,
          width: 44,
          height: 44,
        }}
      >
        {icon}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary" noWrap>
          {label}
        </Typography>
        <Typography variant="h5" fontWeight={700} color={`${color}.main`} noWrap>
          {value}
        </Typography>
        {secondary && (
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {secondary}
          </Typography>
        )}
      </Box>
    </CardContent>
  );

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        borderTop: 3,
        borderTopColor: `${color}.main`,
        transition: "transform 160ms ease, box-shadow 160ms ease",
        ...(onClick && {
          "&:hover": { transform: "translateY(-3px)", boxShadow: 4 },
        }),
      }}
    >
      {onClick ? <CardActionArea onClick={onClick}>{content}</CardActionArea> : content}
    </Card>
  );
}

function SectionHeader({
  icon,
  title,
  subtitle,
  color,
  sx,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  color: KpiColor;
  sx?: object;
}) {
  return (
    <Box sx={{ mt: 4.5, mb: 1.75, display: "flex", alignItems: "center", gap: 1.25, ...sx }}>
      <Avatar
        variant="rounded"
        sx={{
          bgcolor: (theme) => alpha(theme.palette[color].main, 0.14),
          color: `${color}.main`,
          width: 32,
          height: 32,
        }}
      >
        {icon}
      </Avatar>
      <Box>
        <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function Dashboard() {
  const { name, roles, client_id, vendor_id } = useLocalStorage();
  const isAdmin = name === "admin";
  const hasRole = (role: string) => isAdmin || roles?.includes(role);

  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<any>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Dayjs | null>(null);
  const [alert, setAlert] = useState({ open: false, message: "", serverity: "error" as string });
  const categoryGridRef = useRef<AgGridReact>(null);

  const [drillStack, setDrillStack] = useState<DrillDownState[]>([]);
  const drillDown = drillStack[drillStack.length - 1] || null;
  const [detailRows, setDetailRows] = useState<any[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const dateRange = {
    from_date: fromDate ? fromDate.format("YYYY-MM-DD") : undefined,
    to_date: toDate ? toDate.format("YYYY-MM-DD") : undefined,
  };

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const response: any = await getDashboardSummary(client_id, vendor_id, dateRange);
      if (response.status) {
        setSummary(response.data);
        setLastRefreshed(dayjs());
        setAlert((a) => ({ ...a, open: false }));
      } else {
        setSummary(null);
        setAlert({
          open: true,
          message: response.msg || "Failed to load dashboard",
          serverity: "error",
        });
      }
    } catch (error: any) {
      console.log(error);
      setSummary(null);
      setAlert({
        open: true,
        message: error?.message || "Failed to load dashboard",
        serverity: "error",
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client_id, vendor_id, fromDate, toDate]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    if (!drillDown) return;
    const loadDetail = async () => {
      setDetailLoading(true);
      try {
        const response: any = await getDashboardDetail(
          drillDown.type,
          drillDown.filter,
          client_id,
          vendor_id,
          drillDown.params,
          dateRange
        );
        setDetailRows(response?.data || []);
      } catch (error) {
        console.log(error);
        setDetailRows([]);
      } finally {
        setDetailLoading(false);
      }
    };
    loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drillDown]);

  const openDrillDown = (state: DrillDownState) => setDrillStack([state]);
  const drillInto = (state: DrillDownState) => setDrillStack((prev) => [...prev, state]);
  const popDrillDown = () => setDrillStack((prev) => prev.slice(0, -1));
  const navigateDrill = (index: number) => {
    if (index < 0) {
      setDrillStack([]);
      return;
    }
    setDrillStack((prev) => prev.slice(0, index + 1));
  };

  const openOrderLines = (orderNo: string) =>
    drillInto({
      type: "orderLines",
      title: `Order ${orderNo} — Line Items`,
      columns: ORDER_LINE_COLUMNS,
      params: { order_no: orderNo },
    });

  const openShipmentLines = (shipNo: string) =>
    drillInto({
      type: "shipmentLines",
      title: `Shipment ${shipNo} — Line Items`,
      columns: SHIPMENT_LINE_COLUMNS,
      params: { ship_no: shipNo },
    });

  const orderColumns = buildOrderColumns(openOrderLines);
  const pendingShipmentColumns = buildPendingShipmentColumns(openOrderLines);
  const shippingColumns = buildShippingColumns(openShipmentLines);
  const paymentColumns = buildPaymentColumns(openShipmentLines);
  const categoryInvoiceColumns = buildInvoiceLineColumns(openOrderLines, openShipmentLines);

  // Shipment No clicked from the Orders (Net Production) drill-down opens the
  // shipment's master info (same row shape as the "All Shipments" grid),
  // scoped to that one ship_no — not straight to line items. Its own Ship No
  // cell still opens the full line-item detail from there.
  const openShipmentMaster = (shipNo: string) =>
    drillInto({
      type: "shipping",
      title: `Shipment ${shipNo}`,
      subtitle: "Open the Ship No for full line-item detail",
      columns: shippingColumns,
      params: { ship_no: shipNo },
    });

  const categoryOrderColumns = buildCategoryOrderColumns(openOrderLines, openShipmentMaster);
  const categoryTransitColumns = buildCategoryTransitColumns(openOrderLines, openShipmentMaster);

  // Category → Master (one row per art_no), progressed by clicking the
  // category name. At both levels, Article Name and Net Prod. Qty open the
  // orders behind that number, Transit Qty opens undelivered shipments, and
  // Open/Overdue Invoices open the invoice lines — scoped to the row.
  type CategoryScope = { category_id: string; art_no?: string };

  const openCategoryOrders = (scopeLabel: string, scope: CategoryScope) =>
    drillInto({
      type: "categoryOrders",
      title: `${scopeLabel} — Orders (Net Production)`,
      subtitle: "One row per order — open an Order No or Shipment No for detail",
      columns: categoryOrderColumns,
      params: scope,
    });

  const openCategoryTransit = (scopeLabel: string, scope: CategoryScope) =>
    drillInto({
      type: "categoryTransit",
      title: `${scopeLabel} — In-Transit Shipments`,
      subtitle: "Undelivered shipments — open an Order No or Ship No for detail",
      columns: categoryTransitColumns,
      params: scope,
    });

  const openCategoryInvoices = (scopeLabel: string, scope: CategoryScope, filter?: string) =>
    drillInto({
      type: "categoryInvoices",
      filter,
      title: `${scopeLabel} — ${filter === "overdue" ? "Overdue Invoices" : "Open Invoices"}`,
      subtitle: "Open shipment/invoice lines — open an Order No or Ship No for detail",
      columns: categoryInvoiceColumns,
      params: scope,
    });

  const openCategoryMaster = (categoryId: string, categoryName: string) =>
    openDrillDown({
      type: "categoryMaster",
      title: `${categoryName} — Article Master`,
      subtitle:
        "Click Article Name or Net Prod. Qty for orders, Transit Qty for undelivered shipments, or Invoices for payment detail",
      columns: buildMetricColumns(
        [
          { field: "art_no", headerName: "Article No", width: 120, filter: true },
          {
            field: "art_name",
            headerName: "Article Name",
            minWidth: 200,
            flex: 1,
            filter: true,
            cellClass: "drill-link",
            onCellClicked: (p: any) =>
              openCategoryOrders(`${categoryName} — ${p.data.art_name}`, {
                category_id: categoryId,
                art_no: p.data.art_no,
              }),
          },
        ],
        (row) =>
          openCategoryOrders(`${categoryName} — ${row.art_name}`, { category_id: categoryId, art_no: row.art_no }),
        (row) =>
          openCategoryTransit(`${categoryName} — ${row.art_name}`, { category_id: categoryId, art_no: row.art_no }),
        (row, filter) =>
          openCategoryInvoices(
            `${categoryName} — ${row.art_name}`,
            { category_id: categoryId, art_no: row.art_no },
            filter
          )
      ),
      params: { category_id: categoryId },
    });

  const showOrders = hasRole("Order Management") || hasRole("Order List");
  const showPendingShipment = showOrders;
  const showShipping = hasRole("Shipping Info") || hasRole("Shipping List");
  const showPayment = hasRole("Payment Form");
  const showCategory = showOrders || showShipping || showPayment;

  if (drillDown) {
    return (
      <DrillDownView
        onBack={popDrillDown}
        onNavigate={navigateDrill}
        stack={drillStack}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        columns={drillDown.columns}
        rows={detailRows}
        totals={computeTotals(drillDown.type, detailRows)}
        loading={detailLoading}
        detailRowCap={summary?.meta?.detailRowCap || 2000}
      />
    );
  }

  const categoryRows: any[] = summary?.category?.rows || [];
  const categoryTotals: any = summary?.category?.totals || null;
  const ageingBuckets = summary?.payment?.buckets;

  const categoryGridColumns: ColDef[] = buildMetricColumns(
    [
      {
        field: "category_name",
        headerName: "Category",
        minWidth: 160,
        flex: 1,
        filter: true,
        cellClass: "drill-link",
        onCellClicked: (p: any) => openCategoryMaster(p.data.category_id, p.data.category_name),
      },
    ],
    (row) => openCategoryOrders(row.category_name, { category_id: row.category_id }),
    (row) => openCategoryTransit(row.category_name, { category_id: row.category_id }),
    (row, filter) => openCategoryInvoices(row.category_name, { category_id: row.category_id }, filter)
  );

  const money = (n: number | undefined) =>
    (n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

  return (
    <Box sx={{ pb: 3, display: "flex", flexDirection: "column", height: "100%" }}>
      <ERPSnakBar
        open={alert.open}
        message={alert.message}
        serverity={alert.serverity}
        onClose={(open) => setAlert((a) => ({ ...a, open }))}
      />

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
          mb: 2,
        }}
      >
        <Typography variant="h6" fontWeight={700} sx={{ mr: 1 }}>
          Dashboard
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="From"
            format="DD-MM-YYYY"
            value={fromDate}
            onChange={(v) => setFromDate(v)}
            slotProps={{ textField: { size: "small", sx: { width: 160 } } }}
          />
          <DatePicker
            label="To"
            format="DD-MM-YYYY"
            value={toDate}
            onChange={(v) => setToDate(v)}
            slotProps={{ textField: { size: "small", sx: { width: 160 } } }}
          />
        </LocalizationProvider>
        <Button
          size="small"
          variant="text"
          onClick={() => {
            setFromDate(null);
            setToDate(null);
          }}
        >
          All time
        </Button>
        <Button variant="outlined" onClick={loadSummary} disabled={loading}>
          Refresh
        </Button>
        {lastRefreshed && (
          <Typography variant="caption" color="text.secondary">
            As of {lastRefreshed.format("DD-MM-YYYY HH:mm")}
            {fromDate || toDate
              ? ` · Activity ${fromDate ? fromDate.format("DD-MM-YYYY") : "…"} – ${
                  toDate ? toDate.format("DD-MM-YYYY") : "…"
                }`
              : " · All time"}
            {" · Open AR & pending qty are live balances"}
          </Typography>
        )}
      </Box>

      {loading && !summary && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 2.4 }}>
              <Skeleton variant="rounded" height={88} />
            </Grid>
          ))}
          <Grid size={12}>
            <Skeleton variant="rounded" height={220} />
          </Grid>
        </Grid>
      )}

      {!loading && !summary && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={loadSummary}>
              Retry
            </Button>
          }
        >
          Could not load dashboard data.
        </Alert>
      )}

      {summary && (
        <>
          {(showOrders || showShipping || showPayment) && (
            <>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
                Needs attention
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2.5 }}>
                {showOrders && (
                  <Chip
                    icon={<HourglassBottomIcon />}
                    color="warning"
                    variant={summary.orders.dueSoon > 0 ? "filled" : "outlined"}
                    label={`Due soon (7d): ${summary.orders.dueSoon}`}
                    onClick={() =>
                      openDrillDown({
                        type: "orders",
                        filter: "dueSoon",
                        title: "Orders Due Soon (7 days)",
                        columns: orderColumns,
                      })
                    }
                  />
                )}
                {showPayment && (
                  <Chip
                    icon={<WarningAmberIcon />}
                    color="error"
                    variant={summary.payment.overdueAmount > 0 ? "filled" : "outlined"}
                    label={`Overdue: ${money(summary.payment.overdueAmount)} (${
                      summary.payment.overdueCount || 0
                    })`}
                    onClick={() =>
                      openDrillDown({
                        type: "payment",
                        filter: "overdue",
                        title: "Overdue Payments",
                        columns: paymentColumns,
                      })
                    }
                  />
                )}
                {showPayment && ageingBuckets?.d90plus && (
                  <Chip
                    color="error"
                    variant="outlined"
                    label={`90+ days: ${money(ageingBuckets.d90plus.amount)}`}
                    onClick={() =>
                      openDrillDown({
                        type: "payment",
                        filter: "d90plus",
                        title: "Payments 90+ Days Overdue",
                        columns: paymentColumns,
                      })
                    }
                  />
                )}
                {showShipping && (
                  <Chip
                    icon={<DescriptionOutlinedIcon />}
                    color="info"
                    variant={summary.shipping.pendingBl > 0 ? "filled" : "outlined"}
                    label={`Pending BL: ${summary.shipping.pendingBl}`}
                    onClick={() =>
                      openDrillDown({
                        type: "shipping",
                        filter: "pendingBl",
                        title: "Shipments Missing BL",
                        columns: shippingColumns,
                      })
                    }
                  />
                )}
              </Stack>
            </>
          )}

          <SectionHeader icon={<ListAltIcon fontSize="small" />} title="Summary" color="primary" sx={{ mt: 0 }} />
          <Grid container spacing={2}>
            {showOrders && (
              <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                <KpiCard
                  icon={<FilterFramesIcon />}
                  label="Open Orders"
                  value={summary.orders.open}
                  secondary={`Due soon ${summary.orders.dueSoon} · Closed ${summary.orders.closed}`}
                  color="primary"
                  onClick={() =>
                    openDrillDown({
                      type: "orders",
                      filter: "open",
                      title: "Open Orders",
                      columns: orderColumns,
                    })
                  }
                />
              </Grid>
            )}
            {showPendingShipment && (
              <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                <KpiCard
                  icon={<HourglassBottomIcon />}
                  label="Pending Shipment Qty"
                  value={summary.pendingShipment.pendingQty.toLocaleString()}
                  secondary={`${summary.pendingShipment.pendingOrders} orders · Amt ${money(
                    summary.pendingShipment.pendingAmount
                  )}`}
                  color="warning"
                  onClick={() =>
                    openDrillDown({
                      type: "pendingShipment",
                      title: "Orders Pending Shipment",
                      columns: pendingShipmentColumns,
                    })
                  }
                />
              </Grid>
            )}
            {showShipping && (
              <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                <KpiCard
                  icon={<LocalShippingOutlinedIcon />}
                  label="Qty Shipped"
                  value={summary.shipping.qty.toLocaleString()}
                  secondary={`${summary.shipping.total} shipments · Amt ${money(
                    summary.shipping.amount
                  )}`}
                  color="info"
                  onClick={() =>
                    openDrillDown({
                      type: "shipping",
                      title: "All Shipments",
                      columns: shippingColumns,
                    })
                  }
                />
              </Grid>
            )}
            {showPayment && (
              <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                <KpiCard
                  icon={<AccountBalanceWalletIcon />}
                  label="Open Payment Amount"
                  value={money(summary.payment.openAmount)}
                  secondary={`${summary.payment.openInvoices} invoices`}
                  color="success"
                  onClick={() =>
                    openDrillDown({
                      type: "payment",
                      title: "Open Invoices",
                      columns: paymentColumns,
                    })
                  }
                />
              </Grid>
            )}
            {showPayment && (
              <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                <KpiCard
                  icon={<WarningAmberIcon />}
                  label="Overdue Amount"
                  value={money(summary.payment.overdueAmount)}
                  secondary={`${summary.payment.overdueCount || 0} invoices · ${
                    summary.payment.overduePct ?? 0
                  }% of open`}
                  color="error"
                  onClick={() =>
                    openDrillDown({
                      type: "payment",
                      filter: "overdue",
                      title: "Overdue Payments",
                      columns: paymentColumns,
                    })
                  }
                />
              </Grid>
            )}
          </Grid>

          {(showOrders || showShipping) && summary.rates && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              flexWrap="wrap"
              alignItems={{ xs: "flex-start", sm: "center" }}
              gap={2}
              sx={{ mt: 2 }}
            >
              <Chip
                icon={<SpeedIcon />}
                variant="outlined"
                label={`Order fulfillment ${summary.rates.fillRate}% (${Number(
                  summary.rates.shippedQty
                ).toLocaleString()} / ${Number(summary.rates.orderedQty).toLocaleString()} pcs)`}
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <TimelineIcon fontSize="small" color="action" />
                <Typography variant="body2" fontWeight={700}>
                  OTIF {summary.rates.otifRate}% =
                </Typography>
                <Box
                  sx={{
                    display: "inline-flex",
                    flexDirection: "column",
                    alignItems: "center",
                    lineHeight: 1.15,
                    px: 0.5,
                  }}
                >
                  <Typography variant="caption" textAlign="center">
                    on-time shipments ({summary.rates.otifOnTime})
                  </Typography>
                  <Box sx={{ width: "100%", borderTop: 1, borderColor: "text.primary", my: 0.25 }} />
                  <Typography variant="caption" textAlign="center">
                    total shipments with a planned ship date ({summary.rates.otifTotal})
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={700}>
                  × 100
                </Typography>
              </Box>
            </Stack>
          )}

          {showCategory && (
            <>
              <Box sx={{ mt: 4.5, mb: 1.75, display: "flex", alignItems: "center", gap: 1.25 }}>
                <Avatar
                  variant="rounded"
                  sx={{
                    bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.14),
                    color: "secondary.main",
                    width: 32,
                    height: 32,
                  }}
                >
                  <CategoryIcon fontSize="small" />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                      Category Breakdown
                    </Typography>
                    <Tooltip
                      title="Click a Category name for its Article Master. Article Name or Net Prod. Qty opens contributing orders; Transit Qty opens undelivered shipments; Open/Overdue Invoices open shipment lines. Order No / Ship No open full line detail."
                    >
                      <IconButton size="small" aria-label="How category drill works">
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Net production, in-transit qty, and payment/overdue status by category
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <KpiCard
                    icon={<ListAltIcon />}
                    label="Active Categories"
                    value={
                      categoryTotals
                        ? `${categoryTotals.activeCategories}/${categoryTotals.categories}`
                        : 0
                    }
                    color="secondary"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <KpiCard
                    icon={<Inventory2Icon />}
                    label="Net Production Qty"
                    value={(categoryTotals?.net_production_qty || 0).toLocaleString()}
                    color="primary"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <KpiCard
                    icon={<SwapHorizIcon />}
                    label="In-Transit Qty"
                    value={(categoryTotals?.transit_qty || 0).toLocaleString()}
                    color="info"
                  />
                </Grid>
              </Grid>

              <Card
                variant="outlined"
                sx={{ flexGrow: 1, display: "flex", flexDirection: "column", minHeight: 420, mt: 2 }}
              >
                <Box
                  sx={{
                    flexGrow: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    "& .drill-link": { cursor: "pointer", fontWeight: 600 },
                    "& .drill-link:hover": { textDecoration: "underline", color: "primary.main" },
                  }}
                >
                  <ERPGrid
                    ref={categoryGridRef}
                    gridHeight="100%"
                    columnDefs={categoryGridColumns}
                    rowData={categoryRows}
                    defaultColDef={{ resizable: true, sortable: true, wrapHeaderText: false }}
                    pinnedBottomRowData={
                      categoryTotals
                        ? [
                            {
                              category_name: "Total",
                              order_count: categoryTotals.order_count,
                              net_production_qty: categoryTotals.net_production_qty,
                              transit_qty: categoryTotals.transit_qty,
                              open_invoices: categoryTotals.open_invoices,
                              open_amount: categoryTotals.open_amount,
                              overdue_invoices: categoryTotals.overdue_invoices,
                              overdue_amount: categoryTotals.overdue_amount,
                            },
                          ]
                        : undefined
                    }
                    getRowStyle={(params: any) =>
                      params.node?.rowPinned
                        ? {
                            fontWeight: "bold",
                            backgroundColor: "var(--ag-header-background-color)",
                          }
                        : undefined
                    }
                  />
                </Box>
              </Card>
            </>
          )}
        </>
      )}

      {!loading &&
        summary &&
        !showOrders &&
        !showPendingShipment &&
        !showShipping &&
        !showPayment && (
          <Typography sx={{ mt: 3 }} color="text.secondary">
            No dashboard widgets are available for your role yet. Use the menu to access your
            modules.
          </Typography>
        )}
    </Box>
  );
}

export default Dashboard;
